import { Users, Mail, Clock, Loader2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn, timeAgo } from '@/lib/utils'
import { useTenants } from '@/hooks/useApi'

const STATUS_BADGE: Record<string, 'success' | 'danger' | 'warning'> = {
  active: 'success',
  suspended: 'danger',
  pending: 'warning',
}

export function TenantsPanel() {
  const { data, isLoading } = useTenants()
  const tenants = data?.tenants || []

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Tenants
            {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
          </CardTitle>
          <Badge variant="muted">{tenants.length} total</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {tenants.map((tenant) => (
            <div key={tenant.tenant_id} className="flex items-center justify-between p-3 rounded-lg hover:bg-surface-elevated/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className={cn(
                  'h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold',
                  tenant.status === 'active' ? 'bg-accent/10 text-accent' : 'bg-surface-elevated text-text-muted'
                )}>
                  {(tenant.tenant_name || tenant.tenant_id).slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-text-primary">{tenant.tenant_name || tenant.tenant_id}</span>
                    <Badge variant={STATUS_BADGE[tenant.status] || 'muted'} className="text-[10px]">{tenant.status}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-text-muted">
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {tenant.email}
                    </span>
                    <span>Role: {tenant.role}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <div className="flex items-center gap-2 text-[11px] text-text-muted">
                  <Clock className="h-3 w-3" />
                  {timeAgo(new Date(tenant.created_at))}
                </div>
                <div className="flex gap-2 text-[10px]">
                  <span className="text-text-muted">Today: {tenant.requests_today}</span>
                  <span className="text-text-muted">Month: {tenant.requests_month}</span>
                  {tenant.pending_approvals > 0 && (
                    <Badge variant="warning" className="text-[9px]">{tenant.pending_approvals} pending</Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
          {tenants.length === 0 && !isLoading && (
            <div className="text-center py-4 text-sm text-text-muted">No tenants found</div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
